// src/components/admin/mail/mailApi.ts
import api from '../../../api/axiosInstance';
import {
    MailAccount,
    MailAttachment,
    MailFolder,
    MailFolderId,
    MailMessageDetail,
    MailMessageRef,
    MailMessageSummary,
    MailPage,
    MailSendResponse,
} from '../../../types/mail';

const base = (accountId: string) => `/api/mail/${accountId}`;

export const getAccounts = async (): Promise<MailAccount[]> => {
    const res = await api.get('/api/mail/accounts');
    return res.data;
};

export const getFolders = async (accountId: string): Promise<MailFolder[]> => {
    const res = await api.get(`${base(accountId)}/folders`);
    return res.data;
};

export const getMessages = async (
    accountId: string,
    folder: MailFolderId,
    page: number,
    size: number,
    search?: string
): Promise<MailPage<MailMessageSummary>> => {
    const res = await api.get(`${base(accountId)}/messages`, {
        params: { folder, page, size, ...(search ? { search } : {}) },
    });
    return res.data;
};

export const getMessage = async (
    accountId: string,
    folder: MailFolderId,
    uid: number,
    uidValidity: number,
    allowRemoteImages: boolean
): Promise<MailMessageDetail> => {
    const res = await api.get(`${base(accountId)}/messages/${uid}`, {
        params: { folder, uidValidity, allowRemoteImages },
    });
    return res.data;
};

export const setRead = async (
    accountId: string,
    folder: MailFolderId,
    uid: number,
    uidValidity: number,
    seen: boolean
): Promise<void> => {
    await api.patch(`${base(accountId)}/messages/${uid}/flags`, { seen }, { params: { folder, uidValidity } });
};

/** Moves to Trash; deleting from Trash itself expunges permanently. */
export const deleteMessage = async (
    accountId: string,
    folder: MailFolderId,
    uid: number,
    uidValidity: number
): Promise<void> => {
    await api.delete(`${base(accountId)}/messages/${uid}`, { params: { folder, uidValidity } });
};

export interface SendPayload {
    to: string[];
    cc: string[];
    bcc: string[];
    subject: string;
    body: string;
    replyTo: MailMessageRef | null;
    forward: MailMessageRef | null;
    clientRequestId: string;
}

/**
 * Sends a new message, reply or forward.
 *
 * Always multipart, so the same code path covers attachments and no-attachments.
 *
 * The `Content-Type: undefined` header is load-bearing: the shared axios instance sets a
 * default of `application/json`, and with FormData that default survives, so the multipart
 * boundary is never generated and the backend answers 415 with nothing useful in the body.
 */
export const sendMessage = async (
    accountId: string,
    payload: SendPayload,
    attachments: File[],
    onProgress?: (percent: number) => void
): Promise<MailSendResponse> => {
    const form = new FormData();
    form.append('payload', new Blob([JSON.stringify(payload)], { type: 'application/json' }));
    attachments.forEach((file) => form.append('attachments', file));

    const res = await api.post(`${base(accountId)}/messages`, form, {
        headers: { 'Content-Type': undefined },
        onUploadProgress: (e) => {
            if (onProgress && e.total) {
                onProgress(Math.round((e.loaded / e.total) * 100));
            }
        },
    });
    return res.data;
};

/**
 * Downloads an attachment.
 *
 * A plain `<a href download>` cannot carry the Authorization header, so it would trigger an
 * unauthenticated navigation: the backend 401s, and the global 401 interceptor does not even
 * fire (it only sees axios responses), leaving the user with a blank tab and no explanation.
 * Hence the blob round-trip.
 */
export const downloadAttachment = async (
    accountId: string,
    folder: MailFolderId,
    uid: number,
    uidValidity: number,
    attachment: MailAttachment
): Promise<void> => {
    const res = await api.get(
        `${base(accountId)}/messages/${uid}/attachments/${attachment.partId}`,
        { params: { folder, uidValidity }, responseType: 'blob' }
    );

    const blob = new Blob([res.data], { type: attachment.contentType || 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = attachment.fileName || 'priloha';

    // Firefox requires the anchor to be in the DOM before click().
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);

    // Must NOT revoke in the same tick — that races the download start in Safari and
    // Firefox, and the file arrives empty or not at all.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/**
 * `responseType: 'blob'` silently breaks the `err.response?.data?.message` idiom used in
 * every catch block in this codebase — the error body is a Blob, so the property is
 * undefined and every failure would show the generic message.
 */
export const readBlobError = async (err: any): Promise<string | null> => {
    try {
        const text = await err?.response?.data?.text();
        return JSON.parse(text)?.message ?? null;
    } catch {
        return null;
    }
};

/** Pulls the Slovak message out of a normal (non-blob) mailbox error response. */
export const readError = (err: any, fallback: string): string => {
    const data = err?.response?.data;
    if (data?.message) {
        // Bean-validation failures carry the specific field errors alongside the summary.
        if (Array.isArray(data.errors) && data.errors.length > 0) {
            return data.errors.join(' ');
        }
        return data.message;
    }
    return fallback;
};
